import React, { useState } from 'react';
import styled from 'styled-components';

import { Button, Icon } from '@column/column-ui-kit';
import { type DataTableColumnDef, Button as UIButton } from '@column/ui';

import { ROUTE } from '~/app/routes';
import { PopoverFilterEntry } from '~/components';
import { EventDetailModal } from '~/components/Dialog';
import { PaginatedDataTable, getTimestampColumn, useTimeFormat } from '~/components/Table';
import { useNavigate } from '~/lib/navigation';
import {
  EventRepository,
  EventTypes,
  ParsedEvent,
  getEventACHType,
  getEventIcon,
  getEventRoute,
  getEventStatus,
  getEventSummary,
  getRawEventDirection,
  getRawEventType,
  parseEvent,
} from '~/repositories';
import { EventEvent } from '~/typings/API';

type EventsTableRow = ParsedEvent & { action: React.ReactElement };

export const EventMeta = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  justify-content: center;
  margin: -4px 0;
  gap: 4px;
`;

export const EventType = styled.h4`
  flex: auto;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  margin: 0;
  flex-grow: 0;
  padding: 0;
  text-transform: capitalize;
`;

export const EventDetail = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  color: ${({ theme }) => theme.secondary.blendToBackground(600)};
`;

export const EventIcon = styled.div`
  margin-left: -2px;
  display: flex;
  align-items: center;
`;

export const Action = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

export const JSONButton: React.FC<React.ComponentPropsWithoutRef<'button'>> = (props) => (
  <UIButton type="button" variant="secondary" size="xs" {...props} />
);

export const PageEvents: React.FC = () => {
  const navigate = useNavigate();
  const [isEventDetailModalOpen, setIsEventDetailModalOpen] = useState(false);
  const [selectedEventData, setSelectedEventData] = useState<any>(null);
  const { timeFormat, onTimeFormatChange } = useTimeFormat('events');

  const handleOpenEventDetail = (data: any) => {
    setSelectedEventData(data);
    setIsEventDetailModalOpen(true);
  };

  const handleFetch = async (params: any) => {
    return EventRepository.getAll(params).then((response) => {
      const entries = response.events.map((event: EventEvent) => ({
        ...parseEvent(event),
        action: (
          <Action>
            <JSONButton
              onClick={(e) => {
                e.stopPropagation();
                handleOpenEventDetail(event.data);
              }}
            >
              JSON
            </JSONButton>
          </Action>
        ),
      }));

      return { entries, hasMore: Boolean(response?.hasMore) };
    });
  };

  const eventTypes = Object.values(EventTypes).filter((name: string) => !['UNRECOGNIZED'].includes(name));

  const filter: PopoverFilterEntry[] = [
    {
      id: 'types',
      label: 'Type',
      type: 'select',
      list: eventTypes.map((eventType) => ({
        label: eventType,
        value: eventType,
      })),
    },
    {
      id: 'created',
      label: 'Created',
      type: 'date',
    },
  ];

  const columns: DataTableColumnDef<EventsTableRow>[] = [
    {
      header: 'Type',
      accessor: 'data',
      width: 'minmax(300px, auto)',
      cell: ({ value }) => (
        <EventMeta>
          <EventType>
            {getRawEventDirection(value.eventType)} {getRawEventType(value.eventType)} {getEventACHType(value)}
          </EventType>

          <EventDetail>
            <EventIcon>{getEventIcon(value.eventType)}</EventIcon>

            <span>{getEventSummary(value)}</span>
          </EventDetail>
        </EventMeta>
      ),
    },
    {
      header: 'Status',
      accessor: 'eventType',
      width: 'minmax(min-content, 100px)',
      cell: ({ value }) => getEventStatus(value),
    },
    getTimestampColumn<EventsTableRow>({ accessor: 'createdAt', timeFormat, onTimeFormatChange }),
    {
      header: '',
      width: 'min-content',
      accessor: 'action',
      cell: ({ value }) => value,
    },
  ];

  return (
    <>
      <EventDetailModal
        isOpen={isEventDetailModalOpen}
        onClose={() => setIsEventDetailModalOpen(false)}
        data={selectedEventData}
      />

      <PaginatedDataTable<EventsTableRow>
        tableId="events"
        fetch={handleFetch}
        filter={filter}
        empty={{
          icon: <Icon.AnimationSignal />,
          title: 'No events found',
          description: 'Create your first person or a business entity.',
          action: (
            <Button onClick={() => navigate(`${ROUTE.ENTITIES}/edit/person`)} size="small" icon={<Icon.User />}>
              Create Entity
            </Button>
          ),
        }}
        columns={columns}
        rowClassName="min-h-11 py-1"
        onRowClick={(row) =>
          navigate(
            getRawEventType(row.eventType) === 'Identity'
              ? `${getEventRoute(row.eventType)}/${(row.data.subType ?? 'undefined').toLowerCase()}/${row.data.id}`
              : getRawEventType(row.eventType) === undefined
                ? ''
                : `${getEventRoute(row.eventType)}/${row.data.id}`
          )
        }
      />
    </>
  );
};
